import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { CloudOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '../../../ui';

/**
 * "You are offline" — said once, at the top, instead of everywhere at once.
 *
 * This exists because of a failure mode that is easy to miss in development: when
 * the query layer decides the browser is offline it PAUSES retries, which leaves
 * every screen in its pending state with no error to render. Skeletons spin
 * forever and the product looks broken rather than disconnected.
 *
 * So the disconnected state gets its own, honest treatment: one bar, what it
 * means, and a way to try again without reloading.
 */
export function ConnectionNotice() {
  const queryClient = useQueryClient();
  const [online, setOnline] = useState(() => onlineManager.isOnline());

  useEffect(() => onlineManager.subscribe(setOnline), []);

  if (online) return null;

  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center gap-2 border-b border-warning-border bg-warning-bg px-gutter py-1.5 text-xs text-warning"
    >
      <CloudOff className="size-3.5 shrink-0" aria-hidden />
      <span className="font-medium">You appear to be offline.</span>
      <span className="opacity-90">
        Nothing has been lost. Charts and dashboards reload as soon as the connection is back.
      </span>
      <Button
        size="sm"
        variant="secondary"
        className="ml-auto"
        onClick={() => {
          // Tell the query layer to trust the browser again, then re-run what
          // was paused. Cheaper and less destructive than a page reload.
          onlineManager.setOnline(true);
          void queryClient.resumePausedMutations();
          void queryClient.invalidateQueries();
        }}
      >
        Try again
      </Button>
    </div>
  );
}
